import { useState } from 'react';
import { apiConfig } from '../shared/api-config';
import { CourierPackageApi } from '@shared/sdk-v1/apis';

export interface LoopbackFileResult {
  container: string;
  field: string;
  name: string;
  originalFilename: string;
  size: number;
  type: string;
}

interface FileUploadResponse {
  result: {
    files: {
      file: LoopbackFileResult[];
    };
  };
}

interface UseFileUploadReturn {
  uploadFiles: (
    files: File[],
    folder: string,
    courierId: string,
    packageId: string,
    existingFiles?: string[]
  ) => Promise<LoopbackFileResult[]>;
  isUploading: boolean;
  error: Error | null;
}

export function useFileUpload(): UseFileUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const uploadFiles = async (
    files: File[],
    folder: string,
    courierId: string,
    packageId: string,
    existingFiles: string[] = []
  ): Promise<LoopbackFileResult[]> => {
    setIsUploading(true);
    setError(null);

    try {
      const config = apiConfig();
      const uploadedFiles: LoopbackFileResult[] = [];

      // Upload each file
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(
          `${config.basePath}/Uploads/${folder}-${courierId}/upload`,
          {
            method: 'POST',
            body: formData,
            headers: {
              Authorization: `Bearer ${await config.accessToken?.()}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = (await response.json()) as FileUploadResponse;
        uploadedFiles.push(data.result.files.file[0]);
      }

      return uploadedFiles;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Upload failed');
      setError(error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadFiles,
    isUploading,
    error,
  };
}
